import React, { useEffect, useMemo, useState } from 'react'
import { Button, Modal, Select, message } from '@surecompanies/core_components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CarrierGroup,
  createCarrierTasks,
  getCarriers,
} from '../api/fetch-carrier-api'
import styles from '../PlanManagementV3.module.scss'

interface Props {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function FetchCarrierModal({ open, onClose, onSuccess }: Props) {
  const queryClient = useQueryClient()
  const [groupCode, setGroupCode] = useState<CarrierGroup | undefined>(
    undefined
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: carriers, isLoading: isLoadingCarriers } = useQuery({
    queryKey: ['planBotCarriers', 'active'],
    queryFn: () => getCarriers({ isActive: true }),
    enabled: open,
  })

  const carrierOptions = useMemo(() => {
    const list = Array.isArray(carriers) ? carriers : []
    // Group by groupCode first, then exclude any group where at least one entry is running.
    // Filtering by !c.isRunning before deduping would incorrectly show a group that has
    // a non-running year even when another year in the same group is already running.
    const groupMap = new Map<
      string,
      { value: string; label: string; hasRunning: boolean }
    >()
    for (const c of list) {
      const existing = groupMap.get(c.groupCode)
      if (existing) {
        if (c.isRunning) existing.hasRunning = true
      } else {
        groupMap.set(c.groupCode, {
          value: c.groupCode,
          label: c.carrierName ?? c.carrierFullName ?? c.groupCode,
          hasRunning: c.isRunning,
        })
      }
    }
    return Array.from(groupMap.values())
      .filter((g) => !g.hasRunning)
      .map(({ value, label }) => ({ value, label }))
  }, [carriers])

  useEffect(() => {
    if (!open) {
      setGroupCode(undefined)
    }
  }, [open])

  const handleClose = () => {
    if (isSubmitting) return
    onClose()
  }

  const handleFetch = async () => {
    if (!groupCode) return
    setIsSubmitting(true)
    try {
      await createCarrierTasks({ groupCode })
      onSuccess()
      onClose()
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['carrierTasks'] }),
        queryClient.invalidateQueries({ queryKey: ['planBotCarriers'] }),
      ])
    } catch (err) {
      console.error('Failed to create carrier fetch tasks', err)
      message.error('Could not start fetch. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isFetchDisabled = !groupCode || isSubmitting || isLoadingCarriers

  return (
    <Modal
      open={open}
      title="Fetch Carrier"
      maskClosable={false}
      centered
      destroyOnClose
      onCancel={handleClose}
      footer={[
        <Button
          key="cancel"
          htmlType="button"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>,
        <Button
          key="fetch"
          htmlType="button"
          type="primary"
          disabled={isFetchDisabled}
          loading={isSubmitting}
          onClick={() => void handleFetch()}
        >
          Fetch
        </Button>,
      ]}
    >
      <div className={styles.modalContent}>
        <div className={styles.modalField}>
          <label
            className={styles.modalFieldLabel}
            htmlFor="fetch-carrier-group"
          >
            Carrier
          </label>
          <Select
            id="fetch-carrier-group"
            placeholder="Select a carrier"
            value={groupCode}
            onChange={(v) => setGroupCode(v as CarrierGroup)}
            options={carrierOptions}
            loading={isLoadingCarriers}
          />
        </div>
      </div>
    </Modal>
  )
}
